import { fromJS } from 'immutable'
import { compose, propEq, filter, defaultTo } from 'ramda'
import { set } from 'utils/immutable'

import { createReducer } from 'reducers/utils'
import {
  POWER_TAKE_OFF_RECEIVED,
  CLEAR_POWER_TAKE_OFF,
  UPDATE_FRAGMENT_VALUE,
  UPDATE_FRAGMENTS_SUCCESS,
  RESET_FRAGMENTS,
  PTO_TEMPLATES_RECEIVED,
  PTO_TEMPLATE_BY_ID_RECEIVED,
  SELECT_GROUP_PTO_CONFIG,
  RESET_GROUP_PTO_CONFIG,
  UPDATE_PTO_TEMPLATE,
  UPDATE_PTO_TEMPLATE_FRAGMENT_VALUE,
  UPDATE_PTO_TEMPLATE_FRAGMENT_FROM_DEFAULT,
} from 'actions/powerTakeOff'

const initialState = fromJS({
  domains: {},
  fragments: {},
  diagnosticVariant: '',
  templates: {},
  selectedGroupConfig: {},
  selectedTemplate: {},
})

const handlers = {
  [POWER_TAKE_OFF_RECEIVED]: updatePowerTakeOff,
  [CLEAR_POWER_TAKE_OFF]: clearPowerTakeOff,
  [UPDATE_FRAGMENT_VALUE]: updateFragmentValue,
  [UPDATE_FRAGMENTS_SUCCESS]: updatePowerTakeOff,
  [RESET_FRAGMENTS]: resetFragments,
  [PTO_TEMPLATES_RECEIVED]: updatePtoDataByTemplateMeta,
  [PTO_TEMPLATE_BY_ID_RECEIVED]: updatePtoDataByTemplateId,
  [SELECT_GROUP_PTO_CONFIG]: selectGroupPtoConfig,
  [RESET_GROUP_PTO_CONFIG]: resetGroupPtoConfig,
  [UPDATE_PTO_TEMPLATE]: updatePtoTemplate,
  [UPDATE_PTO_TEMPLATE_FRAGMENT_VALUE]: updatePtoTemplateFragmentValue,
  [UPDATE_PTO_TEMPLATE_FRAGMENT_FROM_DEFAULT]:
    updatePtoTemplateFragmentFromDefault,
}

export default createReducer(initialState, handlers)

function updatePowerTakeOff(state, { data }) {
  const {
    entities: { domains, fragments },
    result: { diagnosticVariant },
  } = data
  const setFragments = set('fragments', fragments)
  const setDomains = set('domains', domains)
  const setDiagnosticVariant = set('diagnosticVariant', diagnosticVariant)

  const transform = compose(setFragments, setDomains, setDiagnosticVariant)

  return transform(state)
}

const templateData = (templateIds, fragments, domains, templates) => {
  const defaultTemplateIds = defaultTo([], templateIds)
  const defaultFragments = defaultTo([], fragments)
  const defaultDomains = defaultTo([], domains)
  const defaultTemplates = defaultTo([], templates)

  return defaultTemplateIds.reduce((acc, templateId) => {
    const hasTemplate = propEq('templateId', templateId)
    const fragmentsByTemplate = filter(hasTemplate, defaultFragments)
    const domainsByTemplate = filter(hasTemplate, defaultDomains)
    const templateMetaData = defaultTemplates[templateId]
    const {
      minCompatibleVariant,
      compatibleVariants,
      templateVariant,
      description,
      orgId,
      name,
      partNumber,
      timeStamp,
      author,
      model,
      templateType,
      engineFamily,
      transmissionFamily,
    } = templateMetaData
    return {
      ...acc,
      [templateId]: {
        fragments: fragmentsByTemplate,
        domains: domainsByTemplate,
        templateId,
        templateDescription: description,
        minCompatibleVariant,
        compatibleVariants,
        templateVariant,
        orgId,
        name,
        partNumber,
        timeStamp,
        author,
        model,
        templateType,
        engineFamily,
        transmissionFamily,
      },
    }
  }, {})
}

function updatePtoDataByTemplateMeta(state, payload) {
  const {
    entities: { domains, fragments, templates },
    result: templateNames,
  } = payload?.data
  const templateState = templateData(
    templateNames,
    fragments,
    domains,
    templates
  )
  return set('templates', fromJS(templateState), state)
}

const templateByIdData = (fragments, domains, result) => {
  const {
    minCompatibleVariant,
    compatibleVariants,
    templateVariant,
    description,
    orgId,
    id,
    name,
    partNumber,
    timeStamp,
    author,
    engine,
    transmission,
    isEditing = false,
  } = result
  return {
    [id]: {
      fragments,
      domains,
      templateId: id,
      templateDescription: description,
      minCompatibleVariant,
      compatibleVariants,
      templateVariant,
      orgId,
      name,
      partNumber,
      timeStamp,
      author,
      engine,
      transmission,
      isEditing,
    },
  }
}

function updatePtoDataByTemplateId(state, payload) {
  const { data } = payload
  const {
    entities: { domains, fragments },
    result,
  } = data
  const templateByIdState = templateByIdData(fragments, domains, result)
  return set('selectedTemplate', fromJS(templateByIdState), state)
}

function updatePtoTemplate(
  state,
  { template: { templateId, ...templateProps } }
) {
  const location = ['selectedTemplate', templateId]
  return state.mergeIn(location, { ...templateProps, dirty: true })
}

function updatePtoTemplateFragmentValue(
  state,
  { templateId, fragmentId, value }
) {
  const templateLocation = ['selectedTemplate', templateId]
  const fragment = state.getIn([...templateLocation, 'fragments', fragmentId])
  const isFragmentOutOfRange = !fragment
    .getIn(['configuration', 'definedValues'])
    .includes(value)
  return state.mergeDeepIn(templateLocation, {
    dirty: true,
    changes: {
      [fragmentId]: {
        name: fragment?.get('name'),
        domain: fragment?.get('domain'),
        refNo: fragmentId,
        oldValue: fragment?.getIn(['configuration', 'value']),
        newValue: value,
      },
    },
    fragments: {
      [fragmentId]: {
        pending: true,
        isFragmentOutOfRange,
        configuration: {
          value,
        },
      },
    },
  })
}

function updatePtoTemplateFragmentFromDefault(
  state,
  { templateId, refNo, newValue }
) {
  const templateLocation = ['selectedTemplate', templateId]
  const fragment = state.getIn([...templateLocation, 'fragments', refNo])
  const fromDefaultString = fragment.get('fromDefaultString')

  return state.mergeDeepIn(templateLocation, {
    dirty: true,
    changes: {
      [refNo]: {
        name: fragment?.get('name'),
        domain: fragment?.get('domain'),
        refNo,
        status: fromDefaultString === false ? 'removed' : 'added',
      },
    },
    fragments: {
      [refNo]: {
        fromDefaultString: newValue,
        pending: true,
      },
    },
  })
}

function clearPowerTakeOff() {
  return initialState
}

function updateFragmentValue(state, payload) {
  const { id, value } = payload

  return state.setIn(['fragments', id, 'configuration', 'value'], value)
}

const resetFragment = (fragmentId, state) => {
  const originalValue = state.getIn([
    'fragments',
    fragmentId,
    'configuration',
    'originalValue',
  ])
  return state.setIn(
    ['fragments', fragmentId, 'configuration', 'value'],
    originalValue
  )
}

const updateFragmentReducer = (nextState, id) => resetFragment(id, nextState)

function resetFragments(state, payload) {
  const { fragmentIds } = payload
  const defaultsFragmentIds = defaultTo([], fragmentIds)
  return defaultsFragmentIds.reduce(updateFragmentReducer, state)
}

function selectGroupPtoConfig(state, payload) {
  const { config } = payload

  return state.set('selectedGroupConfig', fromJS(config))
}

function resetGroupPtoConfig(state) {
  return state.set('selectedGroupConfig', fromJS({}))
}
